/**
 * Floating Navbar - Connected to Backend API
 * Uses real wardrobe data for navigation
 */
"use client";

import { Plus } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { usePathname, useRouter } from "next/navigation";
import dynamic from "next/dynamic";
import { useUser } from "@/shared/contexts/UserContext";
import { useDemo } from "@/shared/hooks/use-demo";
import { DemoSignupModal } from "@/components/demo/DemoSignupModal";

// Second static-import path into the cropper/camera chain (the other is
// WardrobeGateContext). The navbar is in AppShell, so this shipped ~97KB of
// cropperjs + camera + catalog-picker JS on every route, and mounted modals whose
// hooks then fetched wardrobe/catalog/basics (481KB) and billing/upload-quota on
// pages like /user/shop. Load it when the user actually taps Add.
const AddDressEntry = dynamic(
  () => import("@/features/wardrobe/modals/AddDressEntry").then((m) => m.AddDressEntry),
  { ssr: false },
);
const FloatingNavbar = () => {
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [addDressModal, setAddDressModal] = useState(false);
  const [addDressEverOpened, setAddDressEverOpened] = useState(false);
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const router = useRouter();
  const pathname = usePathname();
  const { userId } = useUser();
  const { isDemo } = useDemo();
  const dockRef = useRef<HTMLDivElement>(null);

  // Publish the dock's real rendered height to a CSS variable so pages can
  // reserve exactly enough bottom space to clear it. ResizeObserver keeps it
  // correct across OS font-scaling, device sizes, and safe-area changes — the
  // dock grows when labels scale up, and the clearance grows with it.
  useEffect(() => {
    const el = dockRef.current;
    if (!el) return;
    const publish = () =>
      document.documentElement.style.setProperty(
        "--dock-height",
        `${el.offsetHeight}px`,
      );
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const navItems = [
    {
      id: "home",
      label: "Home",
      icon: "/floating-navbar/home.png",
      activeIcon: "/floating-navbar/home-filled.png",
      color: "#ffd372",
      route: "/user/dashboard",
    },
    {
      id: "wardrobe",
      label: "Wardrobe",
      icon: "/floating-navbar/cloth.png",
      activeIcon: "/floating-navbar/cloth-filled.png",
      color: "#ffd372",
      route: "/user/wardrobe",
    },
    {
      id: "coins",
      label: "Coins",
      icon: "/floating-navbar/coin.png",
      activeIcon: "/floating-navbar/coin-filled.png",
      color: "#ffd372",
      route: "/billing/plans",
    },
    {
      id: "profile",
      label: "Profile",
      icon: "/floating-navbar/user.png",
      activeIcon: "/floating-navbar/user-filled.png",
      color: "#ffd372",
      route: "/user/profile",
    },
  ];

  const handleNavItemClick = (item: (typeof navItems)[number]) => {
    setActiveTab(item.id);
    router.push(item.route);
  };

  useEffect(() => {
    if (!pathname) return;

    if (pathname.startsWith("/user/dashboard")) {
      setActiveTab("home");
      return;
    }

    if (pathname.startsWith("/user/wardrobe")) {
      setActiveTab("wardrobe");
      return;
    }

    if (pathname.startsWith("/billing")) {
      setActiveTab("coins");
      return;
    }

    if (pathname.startsWith("/user/profile")) {
      setActiveTab("profile");
      return;
    }

    setActiveTab(null);
  }, [pathname]);

  const handleAddDressClick = () => {
    if (isDemo) {
      setDemoModalOpen(true);
      return;
    }
    setAddDressEverOpened(true);
    setAddDressModal(true);
  };

  return (
    <>
      <div ref={dockRef} className="fixed bottom-4 left-1/2 w-full max-w-[550px] px-4 -translate-x-1/2 z-50">
        <div className="bg-[#fffdfa] backdrop-blur-xl rounded-3xl shadow-2xl border-4 border-[#f0ead8] px-5 py-1 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-[#acd285]/20 via-[#ffd372]/20 to-[#89bff8]/20 animate-pulse rounded-3xl"></div>

          <div className="flex items-center justify-between gap-3 relative z-10">
            {navItems.slice(0, 2).map((item) => {
              const isActive = activeTab === item.id;

              return (
                <button
                  key={item.id}
                  onClick={() => handleNavItemClick(item)}
                  className={`flex flex-col items-center gap-1 transition-all duration-300 ${
                    isActive ? "scale-110" : "hover:scale-105"
                  }`}
                >
                  <div className="flex items-center justify-center w-12 h-12 transition-transform duration-300">
                    <img
                      src={isActive ? item.activeIcon : item.icon}
                      alt={item.label}
                      className="w-7 h-7"
                    />
                  </div>
                  <span
                    className={`text-[11px] font-bold tracking-wide ${
                      isActive ? "" : "text-[#4c4c4c]"
                    }`}
                    style={{ color: isActive ? item.color : undefined }}
                  >
                    {item.label}
                  </span>
                </button>
              );
            })}

            {/* Add Dress Button - Centered */}
            <button
              onClick={handleAddDressClick}
              className="flex flex-col items-center space-y-2 transition-all duration-300 hover:scale-105"
            >
              <div className="p-3 rounded-full bg-[#ffd372] shadow-lg hover:shadow-xl transition-all duration-300 relative group">
                <div className="absolute inset-0 rounded-full bg-[#ffd372] opacity-30 -z-10 group-hover:opacity-50 transition-opacity"></div>
                <Plus className="w-6 h-6 text-[#4c4c4c] font-bold" />
              </div>
              <span className="text-[11px] font-bold tracking-wide text-[#4c4c4c]">
                Add
              </span>
            </button>

            {navItems.slice(2).map((item) => {
              const isActive = activeTab === item.id;

              return (
                <button
                  key={item.id}
                  onClick={() => handleNavItemClick(item)}
                  className={`flex flex-col items-center gap-1 transition-all duration-300 ${
                    isActive ? "scale-110" : "hover:scale-105"
                  }`}
                >
                  <div className="flex items-center justify-center w-12 h-12 transition-transform duration-300">
                    <img
                      src={isActive ? item.activeIcon : item.icon}
                      alt={item.label}
                      className="w-7 h-7"
                    />
                  </div>
                  <span
                    className={`text-[11px] font-bold tracking-wide ${
                      isActive ? "" : "text-[#4c4c4c]"
                    }`}
                    style={{ color: isActive ? item.color : undefined }}
                  >
                    {item.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <DemoSignupModal
        open={demoModalOpen}
        variant="upload"
        onClose={() => setDemoModalOpen(false)}
      />

      {/* Mounted on first open, then left mounted — same as before. Unmounting on
          close would kill an in-flight upload's state; skipping the mount entirely
          until the user taps Add is the free win. */}
      {addDressEverOpened ? (
        <AddDressEntry
          isOpen={addDressModal}
          onClose={() => setAddDressModal(false)}
          wardrobeId={userId || ""}
          onSuccess={() => {
            router.push("/user/wardrobe");
          }}
        />
      ) : null}
    </>
  );
};

export default FloatingNavbar;
